/**
 * The cue order tree: a list of cue ids, where a group carries its children.
 *
 * Two layers live here. The `*Tree` and `addCueToGroup` primitives below
 * MUTATE the array they are given, and each one is only half of a move. The
 * `reorderCues` and `moveCueToGroup` operations at the bottom compose them and
 * are pure, so a half-applied move can never escape.
 */
import type { RundownCueOrderItem } from '~shared/types'

type CueItem = RundownCueOrderItem

// =============== PRIMITIVES (mutate `items` in place)

export const removeCueFromTree = (
  items: CueItem[],
  itemId: string,
): CueItem | null => {
  for (const [index, item] of items.entries()) {
    if (item.id === itemId) {
      const [removed] = items.splice(index, 1)

      return removed ?? null
    }

    if (item.children) {
      const removed = removeCueFromTree(item.children, itemId)

      if (removed) return removed
    }
  }
  return null
}

export const insertCueInTree = (
  items: CueItem[],
  targetId: string,
  item: CueItem,
  insertAfter: boolean = false,
): boolean => {
  for (const [index, current] of items.entries()) {
    if (current.id === targetId) {
      items.splice(insertAfter ? index + 1 : index, 0, item)

      return true
    }

    if (current.children) {
      if (insertCueInTree(current.children, targetId, item, insertAfter)) {
        return true
      }
    }
  }
  return false
}

export const addCueToGroup = (
  items: CueItem[],
  groupId: string,
  itemId: string,
): boolean => {
  // Top level only: groups cannot nest, so a group is never a child.
  const group = items.find((cue) => cue.id === groupId)

  if (!group) return false

  const children = (group.children ??= [])

  children.unshift({ id: itemId })

  return true
}

// =============== OPERATIONS (pure)

/**
 * Move a cue into a group, as its first child.
 *
 * @returns The new order, or the original unchanged if the move is impossible.
 *   Never a partial result: see {@link reorderCues}.
 */
export const moveCueToGroup = (
  cues: CueItem[],
  sourceCueId: string,
  groupId: string,
): CueItem[] => {
  const nextCues = structuredClone(cues)

  const draggedItem = removeCueFromTree(nextCues, sourceCueId)

  if (!draggedItem) return cues

  if (!addCueToGroup(nextCues, groupId, sourceCueId)) return cues

  return nextCues
}

/**
 * Move a cue next to another cue, above it or below it.
 *
 * @remarks
 *   Works on a copy and returns the original if either half of the move fails,
 *   because the two halves are not individually safe: the cue is lifted out of
 *   the tree before the target is located, so a target that cannot be found
 *   (or that was inside the lifted cue) would otherwise leave the cue missing
 *   from the order. That order gets PATCHed to the server, so a partial result
 *   would drop the cue from the rundown.
 *
 * @returns The new order, or the original unchanged if the move is impossible.
 */
export const reorderCues = (
  cues: CueItem[],
  sourceCueId: string,
  targetCueId: string,
  insertAfter: boolean = false,
): CueItem[] => {
  const nextCues = structuredClone(cues)

  const draggedItem = removeCueFromTree(nextCues, sourceCueId)

  if (!draggedItem) return cues

  if (!insertCueInTree(nextCues, targetCueId, draggedItem, insertAfter)) {
    return cues
  }

  return nextCues
}
